import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";
import { formatViews, getTypeLabel } from "@/utils/media";

export default function HeroCarousel({ items }: { items: MediaItem[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((index) => (index + 1) % items.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const goTo = (index: number) => setActive((index + items.length) % items.length);

  return (
    <section className="relative w-full h-[560px] md:h-[680px] overflow-hidden bg-background-950">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${index === active ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <img
            src={item.banner || item.cover}
            alt={`${item.title} 剧照`}
            title={`${item.title} 在线观看`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/70 to-background-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/40" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full max-w-[1280px] mx-auto px-4 md:px-8 flex flex-col justify-center">
        <div key={active} className="max-w-xl animate-fade-up">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-semibold font-label">
              {items[active].tags[0] || "推荐"}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-200/70 backdrop-blur text-foreground-800 text-xs font-medium font-label">
              {getTypeLabel(items[active].type)}
            </span>
            <span className="text-xs text-foreground-600 font-label">
              {items[active].year} · {items[active].region}
            </span>
          </div>

          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-extrabold text-foreground-950 leading-tight tracking-tight">
            {items[active].title}
          </h1>

          <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed line-clamp-3">
            {items[active].description}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-star-fill text-accent-500" />
              评分 <strong className="text-foreground-950 font-semibold">{items[active].rating.toFixed(1)}</strong>
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line text-primary-500" />
              播放 {formatViews(items[active].views)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-star-line" />
              主演 {items[active].actors.slice(0, 2).join("、")}
            </span>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${items[active].id}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </Link>
            <Link
              to={`/play/${items[active].id}/1`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-100/80 backdrop-blur border border-background-300 hover:border-primary-400 text-foreground-950 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-movie-line" />
              查看剧集
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-6 left-0 right-0 px-4 md:px-8 max-w-[1280px] mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到 ${item.title}`}
              onClick={() => goTo(index)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${index === active ? "w-8 bg-primary-500" : "w-3 bg-background-400/70 hover:bg-background-300"}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => goTo(active - 1)}
            className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-100/70 backdrop-blur border border-background-300 text-foreground-950 flex items-center justify-center hover:border-primary-400 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => goTo(active + 1)}
            className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-100/70 backdrop-blur border border-background-300 text-foreground-950 flex items-center justify-center hover:border-primary-400 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}